import type { AppConfig } from "@studystraight/shared-config";
import type { DetectionQuality } from "@studystraight/vision-core";
import { useEffect, useState } from "react";

import type { PerfSnapshot } from "../../platform/vision/InferenceLoop.js";
import { MetricsHud } from "./MetricsHud.js";
import { buildDebugLog, exportDebugLog } from "./exportDebugLog.js";
import type { DebugRecorder } from "./useDebugRecorder.js";

export interface DebugOverlayProps {
  recorder: DebugRecorder;
  perf: PerfSnapshot | null;
  quality: DetectionQuality | null;
  config: AppConfig;
  engine: { engine: string; delegate: "GPU" | "CPU"; delegateNote?: string } | null;
}

/** §25 — panel số liệu + nút export. Skeleton được vẽ riêng, đè lên video trong CameraPreview. */
export function DebugOverlay({
  recorder,
  perf,
  quality,
  config,
  engine,
}: DebugOverlayProps): React.JSX.Element {
  const [exportState, setExportState] = useState<string | null>(null);
  const [, setTick] = useState(0);

  // Ring buffer sống trong ref (không state) nên HUD phải tự nhịp lại theo `uiUpdateFps` (§31),
  // thay vì re-render theo từng frame inference.
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 1000 / config.vision.uiUpdateFps);
    return () => clearInterval(id);
  }, [config.vision.uiUpdateFps]);

  const samples = recorder.snapshot();

  const onExport = (): void => {
    const log = buildDebugLog(
      {
        placement: config.placement,
        engine: engine?.engine ?? "unknown",
        delegate: engine?.delegate ?? "CPU",
        targetFps: perf?.targetFps ?? 0,
        actualFps: perf?.actualFps ?? 0,
        inferenceP50Ms: perf?.inferenceP50Ms ?? 0,
        inferenceP95Ms: perf?.inferenceP95Ms ?? 0,
        droppedFrames: perf?.droppedFrames ?? 0,
        exportedAt: Date.now(),
        userAgent: navigator.userAgent,
      },
      samples,
    );

    exportDebugLog(log)
      .then((how) => setExportState(how === "shared" ? "Đã chia sẻ log." : "Đã tải log về máy."))
      .catch(() => setExportState("Không xuất được log."));
  };

  return (
    <aside className="debug">
      <header className="debug__header">
        <strong>Debug</strong>
        {/* Kèm lý do chọn delegate: trên điện thoại đây là chỗ duy nhất đọc được nó (§25). */}
        <span className="muted">
          {engine
            ? `${engine.engine} · ${engine.delegate}${engine.delegateNote ? ` (${engine.delegateNote})` : ""}`
            : "—"}
        </span>
      </header>

      <MetricsHud
        sample={recorder.latest()}
        samples={samples}
        perf={perf}
        quality={quality}
        config={config}
      />

      <div className="debug__actions">
        <button type="button" className="btn btn--secondary" onClick={onExport}>
          Xuất log 60s
        </button>
        <button
          type="button"
          className="btn btn--ghost"
          onClick={() => {
            recorder.clear();
            setExportState(null);
          }}
        >
          Xoá buffer
        </button>
      </div>
      {exportState ? <p className="muted">{exportState}</p> : null}
      {/* §7 — nhắc ngay trên UI để không ai kỳ vọng log có ảnh. */}
      <p className="muted">Log chỉ gồm số liệu, không chứa hình ảnh.</p>
    </aside>
  );
}
